import type { KameleoonSdkService } from '@core/services/kameleoon/kameleoon-sdk.service';
import type { Tracker, UserOverview } from '@wizbii/utils/models';
import type { TrackingConfig } from '@wizbii/utils/tracking';

import { environment } from '@environment';
import {
  createAwinConfig,
  createBingPixelConfig,
  createChatGptAdsPixelConfig,
  createCriteoPixelConfig,
  createFacebookPixelConfig,
  createGoogleAnalyticsConfig,
  createGoogleRetargetingConfig,
  createHotjarConfig,
  createOutbrainPixelConfig,
  createPinterestPixelConfig,
  createSnapchatPixelConfig,
  createTaboolaPixelConfig,
  createTiktokPixelConfig,
  createTwitterPixelConfig,
  createWizbiiAnalyticsConfig,
} from '@wizbii/utils/tracking';

export const trackingConfig: (
  trackers: Tracker | undefined,
  userOverview: UserOverview | undefined,
  userId?: string,
  kameleoonSdkService?: KameleoonSdkService,
  cookiesVersionDate?: string | null
) => TrackingConfig = (
  trackers: Tracker | undefined,
  userOverview: UserOverview | undefined,
  userId?: string,
  kameleoonSdkService?: KameleoonSdkService,
  cookiesVersionDate?: string | null
): TrackingConfig => {
  return {
    uuidSetterDomain: environment.api.analytics,
    envFqdn: environment.envFqdn,

    consentWidget: {
      version: environment.platform === 'prod' ? '' : environment.platform,
      apiDomain: environment.api.domain,
      wizbiiApiDomain: environment.api.domain,
      consentUrl: environment.api.consent,
      accountUrl: environment.api.account,
      locale: environment.i18n.lang,
      productId: environment.applicationId,
      productLabel: 'WIZBII',
      labels: {},
      cookiesVersionDate: cookiesVersionDate ?? undefined,
    },

    trackers: {
      ...(trackers?.kameleoon?.siteCode && kameleoonSdkService
        ? {
            analytics_kameleoon: kameleoonSdkService.setup(trackers.kameleoon),
          }
        : {}),
      ...(trackers?.googleAnalyticsUai
        ? {
            analytics_wizbii: createWizbiiAnalyticsConfig(trackers.googleAnalyticsUai, environment.api.analytics),
            analytics_google: createGoogleAnalyticsConfig(trackers.googleAnalyticsUai),
          }
        : {}),
      ...(trackers?.gaConversionId
        ? {
            marketing_google: createGoogleRetargetingConfig(trackers.gaConversionId, {
              uniqUserIdOrUserId: userId,
              email: userOverview?.username,
            }),
          }
        : {}),
      ...(!!trackers?.facebookPixel?.pixelId && !!trackers.facebookPixel.pixelVersion
        ? {
            analytics_facebook_pixel: createFacebookPixelConfig({
              appId: trackers.facebookPixel.pixelId,
              version: trackers.facebookPixel.pixelVersion,
              locale: environment.i18n.locale,
              userEmail: userOverview?.username,
            }),
          }
        : {}),
      ...(trackers?.snapchat
        ? {
            analytics_snapchat_pixel: createSnapchatPixelConfig({
              appId: trackers.snapchat,
              userEmail: userOverview?.username,
            }),
          }
        : {}),
      ...(trackers?.bingPixelId
        ? {
            analytics_bing: createBingPixelConfig(trackers.bingPixelId, { userEmail: userOverview?.username }),
          }
        : {}),
      ...(trackers?.tiktok
        ? { analytics_tiktok_pixel: createTiktokPixelConfig(trackers.tiktok, userOverview?.username) }
        : {}),
      ...(!!trackers?.hotjarPixel?.hjid && !!trackers.hotjarPixel.hjsv
        ? {
            analytics_hotjar: createHotjarConfig({
              hjid: trackers.hotjarPixel.hjid,
              hjsv: trackers.hotjarPixel.hjsv,
            }),
          }
        : {}),
      ...(!!trackers?.twitterPixel?.pixelId && !!trackers.twitterPixel.pixelVersion
        ? {
            analytics_twitter: createTwitterPixelConfig({
              twitterId: trackers.twitterPixel.pixelId,
              version: trackers.twitterPixel.pixelVersion,
            }),
          }
        : {}),
      ...(trackers?.awinMerchantId
        ? {
            analytics_awin: createAwinConfig({
              merchantId: trackers.awinMerchantId,
              isTestMode: !environment.production,
            }),
          }
        : {}),
      ...(!!trackers?.pinterestPixel?.pixelId && !!trackers.pinterestPixel.pixelVersion
        ? {
            analytics_pinterest: createPinterestPixelConfig({
              pixelId: trackers.pinterestPixel.pixelId,
              pixelVersion: trackers.pinterestPixel.pixelVersion,
            }),
          }
        : {}),
      ...(!!trackers?.outbrainPixel?.pixelId && !!trackers.outbrainPixel.pixelVersion
        ? {
            analytics_outbrain: createOutbrainPixelConfig({
              pixelId: trackers.outbrainPixel.pixelId,
              pixelVersion: trackers.outbrainPixel.pixelVersion,
            }),
          }
        : {}),
      ...(trackers?.criteoId
        ? {
            analytics_criteo: createCriteoPixelConfig({
              appId: trackers.criteoId,
              userEmail: userOverview?.username,
              productId: environment.applicationId,
            }),
          }
        : {}),
      ...(trackers?.taboolaPixel
        ? {
            analytics_taboola: createTaboolaPixelConfig(trackers.taboolaPixel),
          }
        : {}),
      ...(trackers?.chatGptAdsPixel?.pixelId
        ? {
            marketing_chatgpt: createChatGptAdsPixelConfig({
              pixelId: trackers.chatGptAdsPixel.pixelId,
              userEmail: userOverview?.username,
            }),
          }
        : {}),
    },
    informationGroupKeys: ['technical'],
  };
};
